import React from 'react';

import { Button, ButtonProps, bigButtonStyles } from '@components/Button';
import { shouldReportError, toAppError } from '@helpers/Errors';
import { hasLoggedError, logError } from '@helpers/Loggers';

import { useFormContext } from './context';

export type FormSubmitButtonProps = Omit<ButtonProps, 'onPress'> & {
  /** Keeps a successful submission disabled while the form remains mounted. */
  lockAfterSuccess?: boolean;
  /** Optional TanStack Form submit metadata passed to form.handleSubmit. */
  submitMeta?: unknown;
};

export type FormSubmitter<TSubmitMeta = unknown> = {
  handleSubmit: (submitMeta?: TSubmitMeta) => Promise<void>;
};

export function submitFormSafely<TSubmitMeta>(
  form: FormSubmitter<TSubmitMeta>,
  submitMeta?: TSubmitMeta
) {
  void form.handleSubmit(submitMeta).catch((error) => {
    if (!hasLoggedError(error) && shouldReportError(error)) {
      logError(toAppError(error), { formSubmit: true });
    }
  });
}

export function FormSubmitButton({
  disabled,
  loading,
  lockAfterSuccess = true,
  submitMeta,
  ...props
}: FormSubmitButtonProps) {
  const form = useFormContext();

  return (
    <form.Subscribe
      selector={(state) => ({
        isSubmitSuccessful: state.isSubmitSuccessful,
        isSubmitting: state.isSubmitting,
      })}>
      {({ isSubmitSuccessful, isSubmitting }) => {
        const submitDisabled =
          Boolean(disabled) || isSubmitting || (lockAfterSuccess && isSubmitSuccessful);
        const submitLoading = Boolean(loading) || isSubmitting;

        return (
          <Button
            disabled={submitDisabled}
            loading={submitLoading}
            onPress={() => {
              if (submitDisabled) return;

              submitFormSafely(form, submitMeta);
            }}
            {...props}
          />
        );
      }}
    </form.Subscribe>
  );
}

export function FormSubmitBigButton({ style, labelStyle, ...props }: FormSubmitButtonProps) {
  return (
    <FormSubmitButton
      style={[bigButtonStyles.button, style]}
      labelStyle={[bigButtonStyles.text, labelStyle]}
      {...props}
    />
  );
}
